import { useState } from 'react';
import Reveal from '@/components/base/Reveal';

interface Expert {
  name: string;
  role: string;
  quote: string;
  image: string;
}

type ViewMode = 'scroll' | 'grid';

// Independent traders, analysts and coaches who follow XAUUSD. Their words are
// about the system's process and transparency — not a promise of returns.
// Split into two halves so each marquee row carries a distinct set of people.
const experts: Expert[] = [
  {
    name: 'Marcus Rehn',
    role: 'Prop Firm Trader · 11 yrs on XAUUSD',
    quote:
      'Most gold systems fall apart the moment volatility spikes. This one stayed disciplined through the same stretches my own book did — and the record shows it, trade by trade.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20confident%20middle%20aged%20european%20man%20with%20short%20grey%20hair%20and%20light%20stubble%20wearing%20a%20dark%20shirt%2C%20neutral%20warm%20beige%20background%2C%20soft%20directional%20lighting%2C%20editorial%20headshot%20photography%2C%20sharp%20focus%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-marcus-rehn&orientation=squarish',
  },
  {
    name: 'Elena Vasquez',
    role: 'Quantitative Strategist',
    quote:
      'What won me over is the journal. Nothing is hidden — you can trace every entry back to a rule that fired. That kind of transparency is rarer than the returns themselves.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20latina%20woman%20in%20her%20late%20thirties%20with%20dark%20hair%20pulled%20back%20wearing%20a%20smart%20blazer%2C%20neutral%20warm%20beige%20background%2C%20soft%20even%20lighting%2C%20editorial%20headshot%20photography%2C%20confident%20expression%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-elena-vasquez&orientation=squarish',
  },
  {
    name: 'David Okafor',
    role: 'Funded Account Manager',
    quote:
      'I have watched plenty of accounts survive on luck. This one survives on process. The way it controls risk on every single entry is the part I keep pointing clients to.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20black%20man%20in%20his%20forties%20with%20a%20short%20beard%20wearing%20a%20crisp%20shirt%2C%20neutral%20warm%20beige%20background%2C%20soft%20directional%20lighting%2C%20editorial%20headshot%20photography%2C%20calm%20confident%20expression%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-david-okafor&orientation=squarish',
  },
  {
    name: 'Sofia Lindqvist',
    role: 'Risk & Drawdown Analyst',
    quote:
      'Position sizing stays consistent across the entire record, win or lose. That consistency is why the equity curve reads like a process instead of a guessing game.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20scandinavian%20woman%20with%20blonde%20hair%20and%20glasses%20wearing%20a%20light%20knit%20top%2C%20neutral%20warm%20beige%20background%2C%20soft%20natural%20lighting%2C%20editorial%20headshot%20photography%2C%20friendly%20analytical%20expression%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-sofia-lindqvist&orientation=squarish',
  },
  {
    name: 'Thomas Bergman',
    role: 'Retail Trader · 8 yrs XAUUSD',
    quote:
      'I stopped fighting the gold chop after following this approach. It waits, it executes, it logs. Simple enough that I finally trust what it is doing while I am away.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20scandinavian%20man%20in%20his%20thirties%20with%20short%20dark%20blond%20hair%20wearing%20a%20casual%20button%20shirt%2C%20neutral%20warm%20beige%20background%2C%20soft%20directional%20lighting%2C%20editorial%20headshot%20photography%2C%20relaxed%20confident%20look%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-thomas-bergman&orientation=squarish',
  },
  {
    name: 'Aisha Rahman',
    role: 'Trading Coach & Author',
    quote:
      'I point students here when they want to see what rule-based gold trading looks like in practice. The ledger does half of my teaching for me.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20south%20asian%20woman%20with%20dark%20wavy%20hair%20wearing%20a%20smart%20blouse%2C%20neutral%20warm%20beige%20background%2C%20soft%20even%20lighting%2C%20editorial%20headshot%20photography%2C%20warm%20confident%20smile%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-aisha-rahman&orientation=squarish',
  },
  {
    name: 'Kwame Mensah',
    role: 'Prop Firm Trader · 9 yrs on XAUUSD',
    quote:
      'Two funded challenges in a row is what made me pay attention. The entries are measured and the stop sizes never drift — that is the discipline prop desks actually reward.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20black%20man%20in%20his%20late%20thirties%20with%20a%20clean%20shaven%20head%20wearing%20a%20charcoal%20turtleneck%2C%20neutral%20warm%20beige%20background%2C%20soft%20directional%20lighting%2C%20editorial%20headshot%20photography%2C%20calm%20confident%20gaze%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-kwame-mensah&orientation=squarish',
  },
  {
    name: 'Ingrid Halvorsen',
    role: 'Fund Manager · Macro Desk',
    quote:
      'On a macro desk you see hundreds of gold systems. Few survive an unscheduled volatility event. This one does, and it shows you exactly why in the ledger.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20an%20elegant%20scandinavian%20woman%20in%20her%20fifties%20with%20silver%20blonde%20hair%20wearing%20a%20tailored%20navy%20blazer%2C%20neutral%20warm%20beige%20background%2C%20soft%20even%20lighting%2C%20editorial%20headshot%20photography%2C%20composed%20authoritative%20expression%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-ingrid-halvorsen&orientation=squarish',
  },
  {
    name: 'Rajiv Menon',
    role: 'Algorithmic Trading Engineer',
    quote:
      'I reverse-engineered the rules to understand the logic before trusting it. Every signal traces back to a defined condition — no black box, no hidden discretion.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20south%20asian%20man%20in%20his%20early%20forties%20with%20glasses%20and%20neat%20short%20hair%20wearing%20a%20plain%20grey%20sweater%2C%20neutral%20warm%20beige%20background%2C%20soft%20natural%20lighting%2C%20editorial%20headshot%20photography%2C%20thoughtful%20expression%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-rajiv-menon&orientation=squarish',
  },
  {
    name: 'Claudia Moreau',
    role: 'Drawdown & Risk Reviewer',
    quote:
      'My job is to stress-test the downside. The drawdown behavior here is boring in the best possible way: consistent sizing, defined exits, no revenge trades.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20french%20european%20woman%20in%20her%20forties%20with%20shoulder%20length%20brown%20hair%20wearing%20a%20white%20shirt%2C%20neutral%20warm%20beige%20background%2C%20soft%20directional%20lighting%2C%20editorial%20headshot%20photography%2C%20precise%20serious%20expression%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-claudia-moreau&orientation=squarish',
  },
  {
    name: 'Peter Novak',
    role: 'Retail Swing Trader · 12 yrs XAUUSD',
    quote:
      'Twelve years of gold and I have seen every flavor of hype. This is the first one where the journal is honest enough for me to actually learn from.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20central%20european%20man%20in%20his%20late%20fifties%20with%20thinning%20grey%20hair%20and%20a%20trimmed%20beard%20wearing%20a%20dark%20brown%20jacket%2C%20neutral%20warm%20beige%20background%2C%20soft%20even%20lighting%2C%20editorial%20headshot%20photography%2C%20seasoned%20relaxed%20expression%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-peter-novak&orientation=squarish',
  },
  {
    name: 'Hannah Liebert',
    role: 'Trading Educator & Columnist',
    quote:
      'I write about process over prediction. This system is a rare example where the published record and the rules behind it actually line up.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20german%20woman%20in%20her%20early%20forties%20with%20straight%20light%20brown%20hair%20wearing%20a%20cream%20blouse%2C%20neutral%20warm%20beige%20background%2C%20soft%20directional%20lighting%2C%20editorial%20headshot%20photography%2C%20warm%20intelligent%20expression%2C%20clean%20minimal%20composition&width=200&height=200&seq=expert-hannah-liebert&orientation=squarish',
  },
];

function ExpertCard({ expert, fluid = false }: { expert: Expert; fluid?: boolean }) {
  return (
    <article
      className={`group rounded-lg border border-background-300 bg-background-100 p-6 flex flex-col transition-all duration-300 ease-out will-change-transform hover:-translate-y-1.5 hover:border-primary-300 ${
        fluid ? 'w-full h-full' : 'w-[320px] sm:w-[360px] lg:w-[400px] shrink-0'
      }`}
    >
      <span className="w-8 h-8 flex items-center justify-center text-primary-500">
        <i className="ri-double-quotes-l text-[22px]"></i>
      </span>
      <p className="mt-3 text-[14px] leading-relaxed text-foreground-700 flex-1">{expert.quote}</p>
      <div className="mt-6 pt-5 border-t border-background-200 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full overflow-hidden shrink-0 bg-background-200">
          <img
            src={expert.image}
            alt={`${expert.name} — ${expert.role}`}
            title={`${expert.name}, ${expert.role}`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="min-w-0">
          <p className="font-heading text-[14px] font-semibold text-foreground-950 truncate">
            {expert.name}
          </p>
          <p className="text-[12px] text-foreground-500 truncate">{expert.role}</p>
        </div>
      </div>
    </article>
  );
}

function MarqueeRow({ items, direction }: { items: Expert[]; direction: 'left' | 'right' }) {
  return (
    <div className="relative overflow-hidden">
      <div
        className={`marquee-track flex w-max ${
          direction === 'left' ? 'marquee-left' : 'marquee-right'
        }`}
      >
        <div className="flex gap-5 pr-5 shrink-0">
          {items.map((expert, idx) => (
            <ExpertCard key={`${expert.name}-a-${idx}`} expert={expert} />
          ))}
        </div>
        <div className="flex gap-5 pr-5 shrink-0" aria-hidden="true">
          {items.map((expert, idx) => (
            <ExpertCard key={`${expert.name}-b-${idx}`} expert={expert} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ViewToggle({ view, onChange }: { view: ViewMode; onChange: (mode: ViewMode) => void }) {
  const options: { key: ViewMode; label: string; icon: string }[] = [
    { key: 'scroll', label: 'Scrolling', icon: 'ri-arrow-left-right-line' },
    { key: 'grid', label: 'Grid', icon: 'ri-layout-grid-line' },
  ];

  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-background-200/80 p-1">
      {options.map((opt) => {
        const active = view === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            aria-pressed={active}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[13px] font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 ${
              active
                ? 'bg-background-50 text-foreground-950'
                : 'text-foreground-500 hover:text-foreground-900'
            }`}
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={`${opt.icon} text-[15px]`}></i>
            </span>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ExpertVoices() {
  const [view, setView] = useState<ViewMode>('scroll');

  return (
    <section id="experts" className="relative w-full bg-background-50 overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-primary-200/40 blur-3xl pointer-events-none"></div>

      <div className="relative w-full px-4 md:px-8 lg:px-12 pt-20 md:pt-28 max-w-[1440px] mx-auto">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal>
              <p className="text-[11px] font-data tracking-[0.24em] text-primary-600 mb-5">
                EXPERT VOICES
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[28px] leading-[1.15] sm:text-[38px] lg:text-[44px] max-w-[720px]">
                Endorsed by traders who read the record.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-5 text-[14px] leading-relaxed text-foreground-500 max-w-[560px]">
                Prop traders, analysts and coaches who follow XAUUSD on why this systematic
                approach earned their trust.
              </p>
            </Reveal>
          </div>

          <Reveal delay={180} className="lg:shrink-0">
            <ViewToggle view={view} onChange={setView} />
          </Reveal>
        </div>
      </div>

      <div className="relative mt-12 md:mt-14 pb-20 md:pb-28">
        {view === 'scroll' ? (
          <>
            <MarqueeRow items={experts} direction="left" />

            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-28 bg-gradient-to-r from-background-50 to-transparent"
            ></div>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-28 bg-gradient-to-l from-background-50 to-transparent"
            ></div>
          </>
        ) : (
          <div className="w-full px-4 md:px-8 lg:px-12 max-w-[1440px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {experts.map((expert, idx) => (
                <Reveal key={expert.name} delay={Math.min(idx * 45, 360)} className="h-full flex">
                  <ExpertCard expert={expert} fluid />
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default ExpertVoices;